import { useEffect, useState } from 'react';

// Deterministic, calm accent per app for the monogram fallback.
const APP_TINTS = [
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

function tintFor(id: string): string {
  return APP_TINTS[[...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % APP_TINTS.length];
}

const SIZES = {
  xs: 'h-5 w-5 rounded-md text-[10px]',
  sm: 'h-8 w-8 rounded-lg text-xs',
  md: 'h-11 w-11 rounded-xl text-sm',
  /** sm on phones, md from the sm breakpoint up. */
  'sm-md': 'h-8 w-8 rounded-lg text-xs sm:h-11 sm:w-11 sm:rounded-xl sm:text-sm',
  lg: 'h-14 w-14 rounded-2xl text-base',
} as const;

interface AppLogoProps {
  appId: string;
  displayName: string;
  /** The app's logo as Central serves it to the app switcher. */
  iconUrl?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * An app's real logo — the same file the app switcher shows — so users
 * recognise each app while choosing it. Falls back to the app's monogram when
 * no logo URL is known or the image fails to load. Decorative: the app's name
 * is always rendered beside it.
 */
export function AppLogo({ appId, displayName, iconUrl, size = 'md', className = '' }: AppLogoProps) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [iconUrl]);

  const box = SIZES[size];
  if (iconUrl && !failed) {
    return (
      <img
        src={iconUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setFailed(true)}
        className={`${box} flex-none object-contain ${className}`}
        data-testid={`app-logo-${appId}`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex ${box} flex-none items-center justify-center font-semibold ${tintFor(appId)} ${className}`}
      data-testid={`app-logo-${appId}`}
    >
      {displayName.slice(0, 1)}
    </span>
  );
}
